import { AppEntryController } from '@adapters/controllers/AppEntryController';
import { EntryGateController } from '@adapters/controllers/EntryGateController';
import { PreTriageController } from '@adapters/controllers/PreTriageController';
import { SessionExpiredController } from '@adapters/controllers/SessionExpiredController';
import { SessionLifecycleController } from '@adapters/controllers/SessionLifecycleController';
import { BootstrapController } from '@adapters/controllers/BootstrapController';
import { R4PreTriageApplyTaskBuilder } from '@adapters/fhir/R4PreTriageApplyTaskBuilder';
import { R4PreTriageOutcomeParser } from '@adapters/fhir/R4PreTriageOutcomeParser';
import { R4TriageQuestionnaireParser } from '@adapters/fhir/R4TriageQuestionnaireParser';
import { R4QuestionnaireResponseBuilder } from '@adapters/fhir/R4QuestionnaireResponseBuilder';
import { AppEntryPresenter } from '@adapters/presenters/AppEntryPresenter';
import { EntryGatePresenter } from '@adapters/presenters/EntryGatePresenter';
import { PreTriagePresenter } from '@adapters/presenters/PreTriagePresenter';
import { BootstrapPresenter } from '@adapters/presenters/BootstrapPresenter';
import { DetectNhsAppRuntimeUseCase } from '@core/usecases/DetectNhsAppRuntimeUseCase';
import { GetEntryGateStatusUseCase } from '@core/usecases/GetEntryGateStatusUseCase';
import { GetAppEntryContextUseCase } from '@core/usecases/GetAppEntryContextUseCase';
import { GetBootstrapSummaryUseCase } from '@core/usecases/GetBootstrapSummaryUseCase';
import { ApplyPreTriageTaskUseCase } from '@core/usecases/ApplyPreTriageTaskUseCase';
import { DeterminePreTriageSubmissionTransitionUseCase } from '@core/usecases/DeterminePreTriageSubmissionTransitionUseCase';
import { GetTriageDeferredOptionsUseCase } from '@core/usecases/GetTriageDeferredOptionsUseCase';
import { PreparePreTriageSubmissionAnswersUseCase } from '@core/usecases/PreparePreTriageSubmissionAnswersUseCase';
import { RetryBudgetUseCase } from '@core/usecases/RetryBudgetUseCase';
import { UnresolvedSubmissionUseCase } from '@core/usecases/UnresolvedSubmissionUseCase';
import { ExtendSessionUseCase } from '@core/usecases/ExtendSessionUseCase';
import { GetSessionLifecycleUseCase } from '@core/usecases/GetSessionLifecycleUseCase';
import { LogOutSessionUseCase } from '@core/usecases/LogOutSessionUseCase';
import { BffAppEntryContextHttpGateway } from '@infrastructure/http/BffAppEntryContextHttpGateway';
import { AuthenticatedBffTransport } from '@infrastructure/http/AuthenticatedBffTransport';
import { BffBootstrapHttpGateway } from '@infrastructure/http/BffBootstrapHttpGateway';
import { BffEntryGateHttpGateway } from '@infrastructure/http/BffEntryGateHttpGateway';
import { BffPreTriageHttpGateway } from '@infrastructure/http/BffPreTriageHttpGateway';
import { BffSessionLifecycleHttpGateway } from '@infrastructure/http/BffSessionLifecycleHttpGateway';
import { BffTriageDeferredOptionsHttpGateway } from '@infrastructure/http/BffTriageDeferredOptionsHttpGateway';
import { NhsAppSessionExpiredRecovery } from '@infrastructure/navigation/NhsAppSessionExpiredRecovery';
import { InlineNhsAppGateway } from '@infrastructure/nhsapp/InlineNhsAppGateway';
import { BffJourneyTimingObserver } from '@infrastructure/observability/BffJourneyTimingObserver';
import { RouterService } from '@infrastructure/router/RouterService';
import { JourneyDraftStore } from '@infrastructure/storage/JourneyDraftStore';
import { OriginalOriginStore } from '@infrastructure/storage/OriginalOriginStore';
import type { NhsAppGateway } from '@core/ports/NhsAppGateway';
import type { SessionExpiredRecovery } from '@core/ports/SessionExpiredRecovery';

export type AppEntryControllerContract = Pick<AppEntryController,
  | 'continueFromBeforeYouStart'
  | 'continueFromHandoff'
  | 'handleBack'
  | 'handleRouteChange'
  | 'handleServiceUnavailable'
  | 'loadBeforeYouStartPage'
  | 'loadHandoffPage'
  | 'loadMedicalDeviceInformationPage'
  | 'loadShell'
  | 'loadTermsOfServicePage'
  | 'openHelp'
  | 'openMedicalDeviceInformation'
  | 'openPrivacyPolicy'
  | 'openTermsOfService'
  | 'returnToApp'
  | 'shouldShowBackLink'>;

export type BootstrapControllerContract = Pick<BootstrapController, 'load'>;

export type EntryGateControllerContract = Pick<EntryGateController, 'load'>;

export type PreTriageControllerContract = Pick<PreTriageController,
  | 'getCurrentAnswers'
  | 'load'
  | 'loadDeferredOptions'
  | 'notifyRenderedState'
  | 'retry'
  | 'submit'>;
export type SessionExpiredControllerContract = Pick<SessionExpiredController, 'enterPage' | 'returnToNhsApp'>;

export interface SessionLifecycleControllerContract {
  getSnapshot: SessionLifecycleController['getSnapshot'];
  initialize: SessionLifecycleController['initialize'];
  logOut: SessionLifecycleController['logOut'];
  markSessionRequired: SessionLifecycleController['markSessionRequired'];
  recordActivity: SessionLifecycleController['recordActivity'];
  staySignedIn: SessionLifecycleController['staySignedIn'];
  subscribe: SessionLifecycleController['subscribe'];
  updateFromTiming: SessionLifecycleController['updateFromTiming'];
}

export type NhsAppGatewayContract = Pick<NhsAppGateway, 'detectRuntime'>;

export interface FrontendContainer {
  appEntryController: AppEntryControllerContract;
  bootstrapController: BootstrapControllerContract;
  entryGateController: EntryGateControllerContract;
  preTriageController: PreTriageControllerContract;
  nhsAppGateway: NhsAppGatewayContract;
  router: RouterService;
  sessionExpiredController: SessionExpiredControllerContract;
  sessionLifecycleController: SessionLifecycleControllerContract;
}

let container: FrontendContainer | undefined;

export const createContainer = (): FrontendContainer => {
  if (container) {
    return container;
  }

  let sessionLifecycleControllerRef: SessionLifecycleController | undefined;
  const router = new RouterService(undefined, () => {
    const phase = sessionLifecycleControllerRef?.getSnapshot().phase;
    if (phase === 'blocked' || phase === 'expired' || phase === 'session-required') {
      return '/session-expired';
    }

    return undefined;
  });
  const journeyDraftStore = new JourneyDraftStore();
  const originalOriginStore = new OriginalOriginStore();
  const nhsAppGateway = new InlineNhsAppGateway();
  const nhsAppSessionExpiredRecovery = new NhsAppSessionExpiredRecovery(nhsAppGateway, originalOriginStore);
  const sessionExpiredRecovery: SessionExpiredRecovery & { attemptReturn: () => Promise<boolean> } = {
    returnOrFallback: () => nhsAppSessionExpiredRecovery.returnOrFallback(),
    attemptReturn: async () => {
      await nhsAppSessionExpiredRecovery.returnOrFallback();
      return true;
    },
  };
  const sessionLifecycleGateway = new BffSessionLifecycleHttpGateway();
  const extendSessionUseCase = new ExtendSessionUseCase(sessionLifecycleGateway);
  const getSessionLifecycleUseCase = new GetSessionLifecycleUseCase(sessionLifecycleGateway);
  const logOutSessionUseCase = new LogOutSessionUseCase(sessionLifecycleGateway);
  const sessionLifecycleController = new SessionLifecycleController(getSessionLifecycleUseCase, extendSessionUseCase, logOutSessionUseCase, router);
  sessionLifecycleControllerRef = sessionLifecycleController;
  const authenticatedBffTransport = new AuthenticatedBffTransport(sessionLifecycleController);
  const journeyTimingObserver = new BffJourneyTimingObserver(authenticatedBffTransport);
  const appEntryContextGateway = new BffAppEntryContextHttpGateway(authenticatedBffTransport);
  const appEntryPresenter = new AppEntryPresenter();
  const bootstrapGateway = new BffBootstrapHttpGateway(authenticatedBffTransport);
  const bootstrapPresenter = new BootstrapPresenter();
  const getBootstrapSummaryUseCase = new GetBootstrapSummaryUseCase(bootstrapGateway);
  const entryGateGateway = new BffEntryGateHttpGateway(authenticatedBffTransport);
  const entryGatePresenter = new EntryGatePresenter();
  const getEntryGateStatusUseCase = new GetEntryGateStatusUseCase(entryGateGateway);
  const preTriageGateway = new BffPreTriageHttpGateway(authenticatedBffTransport);
  const triageDeferredOptionsGateway = new BffTriageDeferredOptionsHttpGateway(authenticatedBffTransport);
  const applyPreTriageTaskUseCase = new ApplyPreTriageTaskUseCase(preTriageGateway);
  const getTriageDeferredOptionsUseCase = new GetTriageDeferredOptionsUseCase(triageDeferredOptionsGateway);
  const triageQuestionnaireParser = new R4TriageQuestionnaireParser();
  const triageQuestionnaireResponseBuilder = new R4QuestionnaireResponseBuilder();
  const preTriageApplyTaskBuilder = new R4PreTriageApplyTaskBuilder();
  const preTriageOutcomeParser = new R4PreTriageOutcomeParser();
  const preTriagePresenter = new PreTriagePresenter();
  const preparePreTriageSubmissionAnswersUseCase = new PreparePreTriageSubmissionAnswersUseCase();
  const determinePreTriageSubmissionTransitionUseCase = new DeterminePreTriageSubmissionTransitionUseCase();
  const getAppEntryContextUseCase = new GetAppEntryContextUseCase(appEntryContextGateway);
  const detectNhsAppRuntimeUseCase = new DetectNhsAppRuntimeUseCase(nhsAppGateway);

  const createdContainer: FrontendContainer = {
    appEntryController: new AppEntryController(
      getAppEntryContextUseCase,
      detectNhsAppRuntimeUseCase,
      nhsAppGateway,
      journeyDraftStore,
      originalOriginStore,
      router,
      appEntryPresenter,
    ),
    bootstrapController: new BootstrapController(getBootstrapSummaryUseCase, bootstrapPresenter),
    entryGateController: new EntryGateController(getEntryGateStatusUseCase, entryGatePresenter),
    preTriageController: new PreTriageController(
      applyPreTriageTaskUseCase,
      triageQuestionnaireParser,
      triageQuestionnaireResponseBuilder,
      preTriageApplyTaskBuilder,
      preTriageOutcomeParser,
      preTriagePresenter,
      journeyDraftStore,
      getAppEntryContextUseCase,
      preparePreTriageSubmissionAnswersUseCase,
      determinePreTriageSubmissionTransitionUseCase,
      new RetryBudgetUseCase(journeyDraftStore),
      new UnresolvedSubmissionUseCase(journeyDraftStore),
      getTriageDeferredOptionsUseCase,
      journeyTimingObserver,
    ),
    nhsAppGateway,
    router,
    sessionExpiredController: new SessionExpiredController(sessionLifecycleController, sessionExpiredRecovery, journeyDraftStore),
    sessionLifecycleController,
  };

  container = createdContainer;
  return createdContainer;
};